import {
  LabeledBevestigingInput,
  LabeledCurrencyInput,
  LabeledDateInput,
  LabeledNumberInput,
  LabeledTextInput,
  TextInput
} from "adviesbox-shared";
import { FormikProps } from "formik";
import React, { ReactElement } from "react";
import Modal from "../modal/Modal";
import { HdnAnders, onderpandSchema, OnderpandType } from "../../generic-parts/onderpand/schema";
import AdviesBoxFormik from "../../utils/adviesbox-formik";
import LabeledHdnKeuzelijst from "../hdn-keuzelijst/LabeledHdnKeuzelijst";
import { ISWSideEffects } from "../isw-side-effects/isw-side-effects";
import { getOnderpandTextResources } from "./onderpand-resources";
import { determineOnderpandSideEffects } from "./onderpand-side-effects";

type OnderpandModelContentProps = {
  data: OnderpandType;
  onSave(data: OnderpandType): void;
  closeModal?: () => void;
};

export type OnderpandModalContentState = OnderpandType;

const OnderpandModalContent = ({ data, closeModal, onSave }: OnderpandModelContentProps): ReactElement => {
  return (
    <AdviesBoxFormik<OnderpandModalContentState>
      initialValues={{
        ...data
      }}
      validationSchema={onderpandSchema}
      onSave={onSave}
      closeModal={closeModal}
      render={({ values, submitForm, dirty }: FormikProps<OnderpandModalContentState>): ReactElement => (
        <>
          <ISWSideEffects<OnderpandModalContentState> sync={determineOnderpandSideEffects} />

          <Modal
            title="Onderpand"
            body={
              <>
                <LabeledNumberInput
                  noThousandsSeparator
                  name="bouwjaar"
                  caption="Bouwjaar"
                  labelColSize={6}
                  fieldSize="l"
                />
                <LabeledDateInput name="opleverDatum" caption="Opleverdatum" labelColSize={6} fieldSize="l" />
                <LabeledTextInput name="bagId" caption="BAG-ID" labelColSize={6} />
                <LabeledTextInput name="bouwPlan" caption="Bouwplan" labelColSize={6} />
                <LabeledTextInput name="bouwNummer" caption="Bouwnummer" labelColSize={6} />

                <LabeledTextInput name="kadastraleGemeente" caption="Kadastrale gemeente" labelColSize={6} />

                <LabeledTextInput
                  name="kadastraleSectie"
                  caption="Kadastrale sectie / nummer"
                  type="text"
                  labelColSize={6}
                  fieldSize="m"
                  appendChildren={<TextInput name="kadastraleNummer" type="number" className="px-1" fieldSize="m" />}
                />

                <LabeledBevestigingInput name="gedeeltelijkePerceel" caption="Gedeeltelijk perceel" labelColSize={6} />
                <LabeledBevestigingInput name="appartementsRecht" caption="Appartementsrecht" labelColSize={6} />
                <LabeledCurrencyInput name="serviceKosten" caption="Servicekosten" decimalen={2} labelColSize={6} />
                <LabeledBevestigingInput name="recreatieveBewoning" caption="Recreatieve bewoning" labelColSize={6} />
                <LabeledBevestigingInput name="heeftGarage" caption="Heeft garage" labelColSize={6} />
                <LabeledHdnKeuzelijst
                  caption="Bijzonder objectsoort"
                  name="bijzonderObjectSoort"
                  keuzelijst="BijzObjectSoortType"
                  berichtSoortType="AX"
                  emptyValue={"Selecteer een optie"}
                  labelColSize={6}
                />
                <LabeledHdnKeuzelijst
                  caption="Bouwaard"
                  name="bouwaard"
                  keuzelijst="BouwaardType"
                  berichtSoortType="AX"
                  emptyValue={"Selecteer een optie"}
                  labelColSize={6}
                />
                <LabeledHdnKeuzelijst
                  caption="Staat van onderhoud"
                  name="staatVanOnderhoud"
                  keuzelijst="OnderhoudsToestandType"
                  berichtSoortType="AX"
                  emptyValue={"Selecteer een optie"}
                  labelColSize={6}
                />
                <LabeledBevestigingInput
                  name="verkoopOnderVoorwaarden"
                  caption="Verkoop onder voorwaarden"
                  labelColSize={6}
                />
                {values.verkoopOnderVoorwaarden && (
                  <LabeledHdnKeuzelijst
                    caption="Constructie"
                    name="constructie"
                    keuzelijst="VerkoopOnderVoorwaardenConstructieType"
                    berichtSoortType="AX"
                    labelColSize={6}
                  />
                )}
                {values.verkoopOnderVoorwaarden && values.constructie === `${HdnAnders}` && (
                  <>
                    <LabeledHdnKeuzelijst
                      caption="Constructie anders"
                      name="erfpachtConstructieAnders"
                      keuzelijst="ErfpachtconstructieOfKoperssteunAndersType"
                      berichtSoortType="AX"
                      labelColSize={6}
                    />
                    <LabeledTextInput
                      caption={"Constructie omschrijving"}
                      name={`constructieOmschrijving`}
                      textarea={3}
                      tooltip={getOnderpandTextResources("constructieOmschrijving")}
                      labelColSize={6}
                    />
                  </>
                )}
                <LabeledBevestigingInput
                  name="koopAannemingsOvMetVormAfbouwGarantie"
                  caption="Koop/aannemingsovereenkomst met vorm van afbouwgarantie?"
                  labelColSize={6}
                />
                <LabeledBevestigingInput name="taxatieAanwezig" caption="Taxatie aanwezig" labelColSize={6} />
                {values.taxatieAanwezig && (
                  <LabeledDateInput
                    verplicht={values.taxatieAanwezig}
                    name="taxatieDatum"
                    caption="Taxatiedatum"
                    fieldSize={"l"}
                    labelColSize={6}
                  />
                )}
                <LabeledHdnKeuzelijst
                  caption="Soort taxatie"
                  name="bronwaardeBepaling"
                  keuzelijst="BronWaardebepalingType"
                  berichtSoortType="AX"
                  emptyValue={"Selecteer een optie"}
                  labelColSize={6}
                />
                <LabeledDateInput
                  name="ontbindendeVoorwaardenTot"
                  caption="Ontbindende voorwaarden tot"
                  fieldSize={"l"}
                  labelColSize={6}
                />
              </>
            }
            onSubmitClick={submitForm}
            onCancelClick={closeModal}
            isFormDirty={dirty}
          />
        </>
      )}
    />
  );
};

export default OnderpandModalContent;
